// ─────────────────────────────────────────────────────────────────────────────
// Comparison / mirror provider.
//
// To evaluate a *saved* build without disturbing the current one, we mount a
// second, hidden CharacterProvider + SubStatsProvider (the "mirror") and load a
// build into it. Because it reuses the exact live derivation code, its bonuses /
// substats / damage are computed identically — no math is duplicated.
//
// The mirror tree does NOT render the SkillTree or the EquipmentTab, so this file
// replicates their two side effects headlessly:
//   • skills   → setSkills(build.tempSkills)               (SkillTree's job)
//   • combos   → changeEquipSets(filtered item_combos)     (EquipmentTab's job)
//
// The provider loads the selected saved slot into the mirror (same-job only) and
// exposes its derived bonuses/substats/Player as `compared`. <ComparisonSync/>
// (in the live tree) pushes the current buffs & job up; <ComparisonSelect/> is
// the "Compare with" dropdown. Per decision 4 the mirror uses the CURRENT build's
// buffs on both sides, so the comparison isolates gear/stats/skills.
// ─────────────────────────────────────────────────────────────────────────────

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'

import { CharacterContext, CharacterProvider } from './character'
import { SubStatsContext, SubStatsProvider } from './subStats'
import { DBContext } from './databases'
import { collectComboAegisNames } from '../constants/combo'
import { Player } from '../constants/entity'
import { deserializeBuild } from '../utils/buildSerializer'
import {
	readSlot,
	slotLabel,
	slotHasData,
	MAX_SAVE_SLOTS,
} from '../utils/buildStorage'
import { IS_MALE } from '../data/jobID'

export const ComparisonContext = createContext()

// Keys of the SubStatsContext we snapshot (primitives only — no setters).
const SUBSTAT_KEYS = [
	'maxHP',
	'maxSP',
	'Def',
	'softDef',
	'Mdef',
	'softMdef',
	'hit',
	'flee',
	'perfectDodge',
	'crit',
	'aspd',
	'statusATK',
	'statusMATK',
	'baseWeaponDamage',
	'baseWeaponMagicDamage',
	'refinementBonus',
]

function pickSubstats(sub) {
	const out = {}
	for (const k of SUBSTAT_KEYS) out[k] = sub?.[k]
	return out
}

// Bonuses are stored on an ARRAY that also carries string-keyed props (e.g.
// bAddRace['RC_All'] = 5). JSON.stringify silently drops those, so a naive
// stringify can't see keyed-bonus changes. normalize() captures every own
// enumerable key and collapses empty containers to `undefined` (an empty
// bonus == no bonus), giving a faithful, order-independent signature.
function normalize(v) {
	if (v === null || typeof v !== 'object') return v
	const out = {}
	for (const k of Object.keys(v).sort()) {
		const nv = normalize(v[k])
		if (nv !== undefined) out[k] = nv
	}
	return Object.keys(out).length ? out : undefined
}
function stable(v) {
	return JSON.stringify(normalize(v))
}

// ─── Loads a build into the mirror character provider ─────────────────────────
function MirrorController({ input, buffs }) {
	const {
		changeBaseStat,
		changeJob,
		changeBaseLevel,
		changeJobLevel,
		setTempInventory,
		setSkills,
		setBuffs,
		changeEquipSets,
		inventory,
	} = useContext(CharacterContext)
	const { item_combos, item_db_equip, isDbRefreshed } = useContext(DBContext)

	// Stats / job / levels / skills whenever the target build changes.
	useEffect(() => {
		if (!input) return
		Object.entries(input.stats).forEach(([stat, val]) =>
			changeBaseStat(val, stat),
		)
		changeJob(input.job)
		changeBaseLevel(input.baseLevel)
		changeJobLevel(input.jobLevel)
		// Bypass the (unmounted) SkillTree: the shape is already { AegisName, skillLevel }.
		setSkills(input.tempSkills)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [input])

	// Always (re)apply the target inventory when the build OR the item DB changes.
	// A same-class load can have the SAME item count but different items, so a
	// count-only trigger isn't enough — apply unconditionally here (and reset the
	// retry budget, since a fresh build/DB deserves fresh tries).
	const attemptsRef = useRef(0)
	useEffect(() => {
		attemptsRef.current = 0
		if (!input || !isDbRefreshed) return
		setTempInventory(input.tempInventory)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [input, item_db_equip, isDbRefreshed])

	// Bounded retry ONLY for the async-DB case: the job-filtered DB loads late, so
	// the first rehydration can come up SHORT (fewer items than the build). If so,
	// re-apply (capped) until the correct DB lets every item resolve.
	useEffect(() => {
		if (!input || !isDbRefreshed) return
		const expected = Object.keys(input.tempInventory || {}).length
		const actual = Object.values(inventory).filter((i) => i && i.Id).length
		if (actual < expected && attemptsRef.current < 8) {
			attemptsRef.current++
			setTempInventory(input.tempInventory)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inventory])

	// Buffs applied to the mirror — the current build's buffs (decision 4).
	useEffect(() => {
		setBuffs(buffs || {})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [buffs])

	// Replicate the EquipmentTab's combo detection once the inventory rehydrates.
	useEffect(() => {
		if (!item_combos) return
		changeEquipSets(
			item_combos.filter((combos) =>
				combos.Combos.find((combo) =>
					combo.Combo.every((comb) =>
						collectComboAegisNames(inventory).includes(comb),
					),
				),
			),
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inventory, item_combos])

	return null
}

// ─── Reads the mirror's derived values and lifts them to the store ────────────
function MirrorBridge({ onDerived }) {
	const { bonuses, DEX, INT, skills, extraATK, refreshPlayer } =
		useContext(CharacterContext)
	const sub = useContext(SubStatsContext)

	useEffect(() => {
		const p = refreshPlayer()
		const substats = pickSubstats(sub)
		const player = new Player(
			p.BaseLevel,
			p.JobLevel,
			skills,
			{
				MaxHP: sub.maxHP,
				MaxSP: sub.maxSP,
				DEF: sub.Def,
				SoftDEF: sub.softDef,
				MDEF: sub.Mdef,
				SoftMDEF: sub.softMdef,
				Hit: sub.hit,
				Flee: sub.flee,
			},
			p.Stats,
			bonuses,
			p.Inventory,
		)
		onDerived({ bonuses, DEX, INT, extraATK, substats, player })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bonuses, DEX, INT, sub, skills, extraATK])

	return null
}

export const ComparisonProvider = ({ children }) => {
	// The saved slot compared against (index) or null when the comparison is off.
	const [compareSlot, setCompareSlot] = useState(null)
	// false = apply the CURRENT build's buffs to the mirror (isolate gear/stats);
	// true = use the saved build's OWN buffs (full comparison).
	const [useSavedBuffs, setUseSavedBuffs] = useState(false)
	// The live build's buffs & job, pushed up by <ComparisonSync/> — this provider
	// sits ABOVE the live CharacterProvider so it can't read them directly.
	const [currentBuffs, setCurrentBuffs] = useState({})
	const [liveJob, setLiveJob] = useState(0)
	// The mirror's derived output for the compared build.
	const [compared, setCompared] = useState(null)

	// The build to load into the mirror = the selected slot (deserialized).
	const mirrorInput = useMemo(() => {
		if (compareSlot == null) return null
		const slot = readSlot(compareSlot)
		return slot ? deserializeBuild(slot) : null
	}, [compareSlot])

	// Buffs applied to the mirror: the saved build's own, or the current build's.
	const mirrorBuffs = useSavedBuffs
		? mirrorInput?.buffs || {}
		: currentBuffs

	// Garde-fou: clear the comparison whenever the live job changes. A different-
	// job target is invalid, and this guarantees the mirror never loads a build
	// while the job-filtered item DB is being reloaded.
	const prevJobRef = useRef(liveJob)
	useEffect(() => {
		if (prevJobRef.current !== liveJob) {
			prevJobRef.current = liveJob
			setCompareSlot(null)
		}
	}, [liveJob])

	// Only re-publish when the derived values actually change (avoids render loops).
	// stable() captures the keyed bonus props that JSON.stringify would drop.
	const sigRef = useRef('')
	// Whether a comparison is active. When off, the mirror still holds its last
	// build (MirrorController no-ops on null input), so we must IGNORE its output
	// and clear the display — otherwise a stale comparison keeps showing after a
	// job change clears the selection.
	const activeRef = useRef(false)
	useEffect(() => {
		activeRef.current = compareSlot != null
		if (compareSlot == null) {
			sigRef.current = ''
			setCompared(null)
		}
	}, [compareSlot])

	const onDerived = useCallback((d) => {
		if (!activeRef.current) return
		const sig = stable({ b: d.bonuses, s: d.substats, x: [d.DEX, d.INT] })
		if (sigRef.current === sig) return
		sigRef.current = sig
		setCompared(d)
	}, [])

	return (
		<ComparisonContext.Provider
			value={{
				compareSlot,
				setCompareSlot,
				useSavedBuffs,
				setUseSavedBuffs,
				liveJob,
				setLiveJob,
				setCurrentBuffs,
				compared,
			}}
		>
			{children}
			{/* Hidden mirror tree — reuses the exact live derivation code. Buffs
			    applied to it depend on the "saved buffs" toggle (see mirrorBuffs). */}
			<div style={{ display: 'none' }} aria-hidden='true'>
				<CharacterProvider>
					<SubStatsProvider>
						<MirrorController
							input={mirrorInput}
							buffs={mirrorBuffs}
						/>
						<MirrorBridge onDerived={onDerived} />
					</SubStatsProvider>
				</CharacterProvider>
			</div>
		</ComparisonContext.Provider>
	)
}

// ─────────────────────────────────────────────────────────────────────────────
// Live-side connector: pushes the current build's buffs & job up to the
// comparison store (the provider is above the live CharacterProvider).
// ─────────────────────────────────────────────────────────────────────────────
export function ComparisonSync() {
	const { buffs, job } = useContext(CharacterContext)
	const { setCurrentBuffs, setLiveJob } = useContext(ComparisonContext)
	useEffect(() => {
		setCurrentBuffs(buffs || {})
	}, [buffs, setCurrentBuffs])
	useEffect(() => {
		setLiveJob(job || 0)
	}, [job, setLiveJob])
	return null
}

// ─────────────────────────────────────────────────────────────────────────────
// The "Compare with ▾" selector — same instance-of-state in every panel, so all
// copies stay synced. Only same-job saved slots are offered (decision 3).
// ─────────────────────────────────────────────────────────────────────────────
export function ComparisonSelect() {
	const {
		compareSlot,
		setCompareSlot,
		useSavedBuffs,
		setUseSavedBuffs,
		liveJob,
	} = useContext(ComparisonContext)

	const options = useMemo(() => {
		const strip = (j) => (j || 0) & ~IS_MALE
		const out = []
		for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
			const slot = readSlot(i)
			if (slotHasData(slot) && strip(slot.job) === strip(liveJob)) {
				out.push({ value: i, label: slotLabel(i, slot) })
			}
		}
		return out
	}, [liveJob])

	return (
		<div className='comparisonSelect'>
			<div className='comparisonTitle'>Compare with</div>
			<div className='comparisonRow'>
				<select
					value={compareSlot ?? ''}
					onChange={(e) =>
						setCompareSlot(
							e.target.value === ''
								? null
								: Number(e.target.value),
						)
					}
				>
					<option value=''>None</option>
					{options.map((o) => (
						<option key={o.value} value={o.value}>
							{o.label}
						</option>
					))}
				</select>
				{compareSlot != null && (
					<button
						type='button'
						className='comparisonClear'
						title='Stop comparing'
						onClick={() => setCompareSlot(null)}
					>
						×
					</button>
				)}
			</div>
			{compareSlot != null && (
				<label
					className='comparisonBuffs'
					title="On: use the saved build's own buffs (full comparison). Off: apply the current build's buffs to both (isolates gear/stats)."
				>
					<input
						type='checkbox'
						checked={useSavedBuffs}
						onChange={(e) => setUseSavedBuffs(e.target.checked)}
					/>
					Saved build's buffs
				</label>
			)}
		</div>
	)
}
